import * as React from 'react'
import { debounce } from 'lodash'
import { Mail, X } from 'lucide-react'

import { Input } from '@/components/ui'
import { usePostListUsers } from '@/features/user'
import { cn } from '@/utils'
import { SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'

import { FIELD_LIMITS } from '../../constants'
import { useUserManagementStore } from '../../store'
import { IInviteUser } from '../../types'
import { getEmailError } from '../../utils'

type InviteUserFormRowProps = {
  /** User entry data */
  entry: IInviteUser

  /** Callback when any field is updated */
  onUpdate: (updates: Partial<IInviteUser>) => void

  /** Callback when user row is removed */
  onRemove: () => void

  /** API error for this row (e.g. invitation send failed) */
  invitationError?: string

  /** Whether remove button should be disabled (only 1 user) */
  disableRemove?: boolean

  /** Index of the user row */
  index: number
}

const InviteUserFormRow: React.FC<InviteUserFormRowProps> = (props) => {
  const {
    entry,
    onUpdate,
    onRemove,
    invitationError,
    disableRemove = false,
    index,
  } = props

  const duplicateEmailsInBatch = useUserManagementStore(
    (state) => state.duplicateEmailsInBatch
  )
  const { setInvitationErrors, setIsCheckingEmail } = useUserManagementStore()

  const { mutate: postListUsers, isPending: isCheckingEmail } =
    usePostListUsers()

  // Field change handlers
  const handleFirstNameChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate({ firstName: e.target.value })
    },
    [onUpdate]
  )

  const handleLastNameChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate({ lastName: e.target.value })
    },
    [onUpdate]
  )

  const handleEmailChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate({ email: e.target.value })
    },
    [onUpdate]
  )

  const handleRemove = React.useCallback(() => {
    if (disableRemove) return
    onRemove()
  }, [disableRemove, onRemove])

  // Email validation: format + duplicate-in-batch (from utils)
  const emailError = React.useMemo(
    () => getEmailError(entry.email, duplicateEmailsInBatch),
    [entry.email, duplicateEmailsInBatch]
  )

  const emailAndIndexRef = React.useRef({ email: entry.email, index })

  React.useEffect(() => {
    emailAndIndexRef.current = { email: entry.email, index }
  }, [entry.email, index])

  // Enhanced debounced request for checking email duplication
  const emailDuplicateDebounced = React.useMemo(
    () =>
      debounce(() => {
        const { email, index: idx } = emailAndIndexRef.current
        postListUsers(
          { data: { q: email, limit: 1 } },
          {
            onSuccess: ({ data }) => {
              setInvitationErrors({
                [idx]:
                  data.length > 0 ? 'This email already exists' : undefined,
              })
            },
          }
        )
      }, SEARCH_DEBOUNCE_DELAY_MS),
    [postListUsers, setInvitationErrors]
  )

  React.useEffect(() => {
    // If there is no email or email error, don't check for duplicate email
    if (!entry.email || emailError) return
    // Check for duplicate email
    emailDuplicateDebounced()
    return () => emailDuplicateDebounced.cancel()
  }, [entry.email, emailError, emailDuplicateDebounced])

  React.useEffect(() => {
    setIsCheckingEmail(isCheckingEmail)
  }, [isCheckingEmail, setIsCheckingEmail])

  // Show either inline validation error or API invitation error
  const displayError = emailError || invitationError

  return (
    <div className="flex gap-2 items-end">
      <div className="flex gap-4 flex-[1_1_auto]">
        {/* First name */}
        <div className="space-y-1.5 w-1/4">
          <Input
            id={`firstName-${entry.uuid}`}
            value={entry.firstName}
            onChange={handleFirstNameChange}
            placeholder="Enter first name"
            maxLength={FIELD_LIMITS.NAME_MAX_LENGTH}
          />
        </div>

        {/* Last name */}
        <div className="space-y-1.5 w-1/4">
          <Input
            id={`lastName-${entry.uuid}`}
            value={entry.lastName}
            onChange={handleLastNameChange}
            placeholder="Enter last name"
            maxLength={FIELD_LIMITS.NAME_MAX_LENGTH}
          />
        </div>

        {/* Email */}
        <div className="space-y-1.5 w-1/2">
          <Input
            id={`email-${entry.uuid}`}
            type="email"
            value={entry.email}
            onChange={handleEmailChange}
            placeholder="Enter email"
            maxLength={FIELD_LIMITS.EMAIL_MAX_LENGTH}
            className={displayError ? 'border-red-500' : ''}
            leadingIcon={
              <Mail
                size={16}
                className="size-4 text-foreground-fg-quaternary-400"
              />
            }
          />
          {displayError && (
            <p
              id={`email-error-${entry.uuid}`}
              className="text-xs text-red-600"
            >
              {displayError}
            </p>
          )}
        </div>
      </div>
      <button
        type="button"
        className={cn(
          'flex-[0_0_40px] size-10 border-none bg-transparent',
          disableRemove || isCheckingEmail
            ? 'opacity-50 cursor-not-allowed'
            : 'cursor-pointer',
          displayError ? 'mb-6' : ''
        )}
        disabled={disableRemove || isCheckingEmail}
        onClick={handleRemove}
      >
        <X size={20} className="size-5 text-foreground-fg-quaternary-400" />
      </button>
    </div>
  )
}

const MemoizedInviteUserFormRow = React.memo(InviteUserFormRow)
MemoizedInviteUserFormRow.displayName = 'InviteUserFormRow'

export { MemoizedInviteUserFormRow as InviteUserFormRow }
